import React, { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  Button,
  DialogContent,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import axios from "axios";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  LoginRounded,
  VisibilityOffRounded,
  VisibilityRounded,
} from "@mui/icons-material";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { CloseRounded } from "@mui/icons-material";
import PropTypes from "prop-types";
import { Input as BaseInput } from "@mui/base/Input";
import { debounce, getUserIpWithCountry } from "../styles/styles";
import {
  applyAuthSession,
  persistPendingAuthSession,
} from "../redux/Apps/users/authSession";
import companyLogo from "../assets/nav-lexmom.webp";
import signupImg from "../assets/sign-up.webp";
import emailSend from "../assets/email_sent.webp";
import RetroGridBg from "../components/RetroGridBg";
import { backend_api } from "../static/server";
import wave_lins from "../assets/wave-line.png";
import SocialLinkBtn from "../components/Auth/SocialLinkBtn";

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

function OTP({ separator, length, value, onChange }) {
  const inputRefs = useRef(new Array(length).fill(null));

  const focusInput = (targetIndex) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.focus();
  };

  const selectInput = (targetIndex) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.select();
  };

  const handleKeyDown = (event, currentIndex) => {
    switch (event.key) {
      case "ArrowUp":
      case "ArrowDown":
      case " ":
        event.preventDefault();
        break;
      case "ArrowLeft":
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }
        break;
      case "ArrowRight":
        event.preventDefault();
        if (currentIndex < length - 1) {
          focusInput(currentIndex + 1);
          selectInput(currentIndex + 1);
        }
        break;
      case "Delete":
        event.preventDefault();
        onChange((prevOtp) => {
          const otp =
            prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
          return otp;
        });

        break;
      case "Backspace":
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }

        onChange((prevOtp) => {
          const otp =
            prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
          return otp;
        });
        break;

      default:
        break;
    }
  };

  const handleChange = (event, currentIndex) => {
    const currentValue = event.target.value;
    let indexToEnter = 0;

    while (indexToEnter <= currentIndex) {
      if (
        inputRefs.current[indexToEnter].value &&
        indexToEnter < currentIndex
      ) {
        indexToEnter += 1;
      } else {
        break;
      }
    }
    onChange((prev) => {
      const otpArray = prev.split("");
      const lastValue = currentValue[currentValue.length - 1];
      otpArray[indexToEnter] = lastValue;
      return otpArray.join("");
    });
    if (currentValue !== "") {
      if (currentIndex < length - 1) {
        focusInput(currentIndex + 1);
      }
    }
  };

  const handleClick = (event, currentIndex) => {
    selectInput(currentIndex);
  };

  const handlePaste = (event, currentIndex) => {
    event.preventDefault();
    const clipboardData = event.clipboardData;

    // Check if there is text data in the clipboard
    if (clipboardData.types.includes("text/plain")) {
      let pastedText = clipboardData.getData("text/plain");
      pastedText = pastedText.substring(0, length).trim();
      let indexToEnter = 0;

      while (indexToEnter <= currentIndex) {
        if (
          inputRefs.current[indexToEnter].value &&
          indexToEnter < currentIndex
        ) {
          indexToEnter += 1;
        } else {
          break;
        }
      }

      const otpArray = value.split("");

      for (let i = indexToEnter; i < length; i += 1) {
        const lastValue = pastedText[i - indexToEnter] ?? " ";
        otpArray[i] = lastValue;
      }
      onChange(otpArray.join(""));
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: { xs: "0.3rem", md: "0.5rem" },
        alignItems: "center",
        width: 1,
        justifyContent: "center",
      }}
    >
      {new Array(length).fill(null).map((_, index) => (
        <React.Fragment key={index}>
          <BaseInput
            slots={{
              input: InputElement,
            }}
            aria-label={`Digit ${index + 1} of OTP`}
            slotProps={{
              input: {
                ref: (ele) => {
                  inputRefs.current[index] = ele;
                },
                onKeyDown: (event) => handleKeyDown(event, index),
                onChange: (event) => handleChange(event, index),
                onClick: (event) => handleClick(event, index),
                onPaste: (event) => handlePaste(event, index),
                value: value[index] ?? "",
              },
            }}
          />
          {index === length - 1 ? null : (
            <Typography
              variant="body1"
              component="span"
              color="text.secondary"
              sx={{ fontSize: "0.8rem", fontWeight: 500 }}
            >
              {separator}
            </Typography>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

OTP.propTypes = {
  length: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  separator: PropTypes.node,
  value: PropTypes.string.isRequired,
};

const SignupPage = ({ pricePagePopup, onSuccess, prepareCheckoutContext }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [encryptedKey, setEncryptedKey] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [openForgotPass, setOpenForgotPass] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [userExist, setUserExist] = useState(null);
  const [passwordError, setPasswordError] = useState("");
  const [otp, setOtp] = useState("");
  const [country, setCountry] = useState(null);

  useEffect(() => {
    getUserIpWithCountry().then((data) => {
      if (data) {
        setCountry(data.country_code);
      }
    });
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      otp: "",
      user_type: "lawyer",
    },
  });

  const emailValue = watch("email");
  const password = watch("password");
  const confirmPassword = watch("confirmPassword");

  useEffect(() => {
    if (userExist === false && password && confirmPassword) {
      if (password !== confirmPassword) {
        setPasswordError("Passwords do not match");
      } else {
        setPasswordError("");
      }
    }
  }, [password, confirmPassword, userExist]);

  const handleCheckEmail = useCallback(
    debounce(async (email) => {
      if (!email || !EMAIL_REGEX.test(email)) {
        setUserExist(null);
        return;
      }

      setIsLoading(true);
      try {
        const response = await axios.get(
          `${backend_api}user/check-mail/?email=${email}`,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );
        setUserExist(response?.data?.exist);
      } catch (error) {
        console.error("Error checking email:", error);
        toast.error("Error checking email availability");
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    if (emailValue) {
      handleCheckEmail(emailValue);
    }
  }, [emailValue, handleCheckEmail]);

  const {
    register: forgotRegister,
    handleSubmit: forgotHandleSubmit,
    reset: forgotReset,
    setValue: forgotSetValue,
    watch: forgotWatch,
    formState: { errors: forgotErrors },
  } = useForm({
    defaultValues: {
      email: emailValue || "",
    },
  });

  useEffect(() => {
    forgotSetValue("email", emailValue);
  }, [emailValue, forgotSetValue]);

  const setupSession = useCallback(
    (responseData) => {
      if (!responseData) {
        toast.error("Invalid response data from server.");
        return null;
      }

      return applyAuthSession(dispatch, responseData, {
        clearPending: true,
      })?.tokenValue;
    },
    [dispatch],
  );

  const handleLogin = async (data) => {
    setIsLoading(true);
    const checkoutContext =
      pricePagePopup &&
      typeof onSuccess === "function" &&
      userExist !== false &&
      typeof prepareCheckoutContext === "function"
        ? prepareCheckoutContext()
        : null;

    try {
      const body = new URLSearchParams();
      body.append("email", data?.email);
      body.append("password", data?.password);
      body.append("user_type", "lawyer");

      const response = await axios.post(`${backend_api}user/signup/`, body, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      const responseData = response?.data?.data;
      if (responseData?.user_type === "lawyer") {
        if (pricePagePopup && typeof onSuccess === "function") {
          persistPendingAuthSession(responseData);
          onSuccess(checkoutContext);
          // toast.success("Login successful!");
        } else {
          setupSession(responseData);
          toast.success("Login successful!");
          navigate("/dashboard", { replace: true });
        }
      } else if (responseData?.encrypt_key) {
        setEncryptedKey(responseData?.encrypt_key);
        setShowOtpInput(true);
        toast.info("Please verify your email with OTP");
      }
    } catch (error) {
      if (checkoutContext?.paymentWindow && !checkoutContext.paymentWindow.closed) {
        checkoutContext.paymentWindow.close();
      }
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Network or server error. Please try again.");
      }
      console.error("Login error", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (otp.length === 6) {
      const currentFormValues = getValues();
      handleOtpVerify({
        ...currentFormValues,
        otp: otp,
      });
    }
  }, [otp]);

  const handleOtpVerify = async (data) => {
    setIsOtpLoading(true);
    const checkoutContext =
      pricePagePopup &&
      typeof onSuccess === "function" &&
      typeof prepareCheckoutContext === "function"
        ? prepareCheckoutContext()
        : null;

    try {
      const body = new URLSearchParams();
      body.append("email", data?.email);
      body.append("otp", otp);
      body.append("encrypted_key", encryptedKey);
      body.append("user_type", "lawyer");
      body.append("country", country);

      const response = await axios.post(`${backend_api}otp-verify`, body, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      const responseData = response?.data?.data;
      // console.log("otp", response);
      if (responseData?.user_type === "lawyer") {
        if (pricePagePopup && typeof onSuccess === "function") {
          persistPendingAuthSession(responseData);
          onSuccess(checkoutContext);
          // toast.success("Sign up completed successfully!");
        } else {
          setupSession(responseData);
          toast.success("Sign up completed successfully!");
          navigate("/dashboard", { replace: true });
        }
      }
    } catch (error) {
      if (checkoutContext?.paymentWindow && !checkoutContext.paymentWindow.closed) {
        checkoutContext.paymentWindow.close();
      }
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Network or server error. Please try again.");
      }
      console.error("OTP Verification Error", error);
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setValue(name, value);
    if (userExist === false) {
      trigger("confirmPassword");
    }
  };

  const handleForgotPassword = async (data) => {
    setIsLoading(true);
    setValue("email", data?.email);
    try {
      const response = await axios.get(
        `${backend_api}user/forget-password/?send_reset_link&email=${data?.email}`,
        data
      );
      toast.success(response?.data?.message);
      setIsEmailSent(true);
    } catch (error) {
      console.error("Forgot Password Error", error);
    }
  };

  const handleCloseForgotPass = () => {
    setOpenForgotPass(false);
    setIsEmailSent(false);
    setIsLoading(false);
  };

  const handleFocus = () => {
    const currentValue = getValues("email");
    if (!currentValue) {
      setValue("email", " ");
    }
  };

  const handleBlur = (event) => {
    const { value } = event.target;
    setValue("email", value.trim());
  };

  const getFormActionText = () => {
    if (userExist === null) return "Login";
    return userExist ? "Login" : "Sign Up";
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column-reverse", md: "row" },
        p: { xs: 0, md: 0 },
      }}
    >
      <Box
        component={motion.div}
        initial={{ opacity: 0, x: -100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
        sx={{
          width: { xs: 1, md: pricePagePopup ? 1 : 1 / 2 }, // Full width on mobile
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: { xs: 2, md: 3 }, // Reduce padding on mobile
          overflow: "hidden",
          position: "relative",
          borderRadius: { xs: "1rem", md: "2rem" }, // Smaller radius on mobile
          minHeight: { xs: "100vh", md: "auto" },
        }}
      >
        <Box
          sx={{
            width: 1,
            maxWidth: "27.5rem",
            p: { xs: 2, md: 3 }, // Reduce padding on mobile
            zIndex: 5,
            borderRadius: 2,
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            border: "1px solid",
            borderColor: "divider",
            transition: "all 0.3ms",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: { xs: 2, md: 4 },
            }}
          >
            <Box
              sx={{
                height: { xs: "2.5rem", md: "3rem" },
                mb: 1,
                cursor: "pointer",
              }}
              onClick={() => navigate("/")}
            >
              <img
                src={companyLogo}
                alt="Company Logo"
                style={{
                  height: "100%",
                  width: "auto",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  e.onerror = null;
                  e.target.src = require("../assets/nav-lexmom.webp");
                }}
              />
            </Box>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
              Log In / Sign Up
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Enter your credentials to access your account
            </Typography>
          </Box>

          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: "1rem", md: "0.9rem" },
              width: 1,
              maxWidth: "sm",
              "& > *": { mb: { xs: 1, md: 2 } },
            }}
            onSubmit={handleSubmit((data, e) => {
              e?.stopPropagation();
              e?.preventDefault();
              if (encryptedKey) {
                handleOtpVerify(data);
              } else {
                handleLogin(data);
              }
            })}
          >
            <TextField
              size="small"
              fullWidth
              label="Email"
              type="email"
              variant="outlined"
              error={!!errors.email}
              helperText={errors.email?.message}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
              InputLabelProps={{
                shrink: !!emailValue || emailValue === " ",
              }}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={(e) => handleCheckEmail(e.target.value || emailValue)}
            />
            <Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  name="password"
                  onChange={handlePasswordChange}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 6,
                      message: "Password must be at least 6 characters",
                    },
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" size="small">
                        <IconButton
                          onClick={handleClickShowPassword}
                          edge="end"
                          color="primary"
                        >
                          {showPassword ? (
                            <VisibilityOffRounded sx={{ fontSize: "1rem" }} />
                          ) : (
                            <VisibilityRounded sx={{ fontSize: "1rem" }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {userExist === false && (
                  <TextField
                    size="small"
                    fullWidth
                    label="Confirm Password"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    name="confirmPassword"
                    onChange={handlePasswordChange}
                    error={!!errors.confirmPassword || !!passwordError}
                    helperText={
                      errors.confirmPassword?.message || passwordError
                    }
                    {...register("confirmPassword", {
                      validate: (value) =>
                        value === watch("password") || "Passwords do not match",
                    })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" size="small">
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                            color="primary"
                          >
                            {showPassword ? (
                              <VisibilityOffRounded sx={{ fontSize: "1rem" }} />
                            ) : (
                              <VisibilityRounded sx={{ fontSize: "1rem" }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Box>

              {userExist || userExist === null ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    mt: 1,
                  }}
                >
                  <Typography
                    onClick={() => setOpenForgotPass(true)}
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      cursor: "pointer",
                      textDecoration: "underline",
                      fontWeight: 500,
                      "&:hover": {
                        textDecoration: "none",
                      },
                    }}
                  >
                    Forgot password?
                  </Typography>
                </Box>
              ) : null}
            </Box>
            {showOtpInput && (
              <Box sx={{ width: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    fontSize: {
                      xs: "0.8rem",
                      md: "0.875rem",
                      textAlign: "center",
                    },
                  }}
                >
                  Enter the verification code sent to your email
                </Typography>
                <OTP
                  separator={<span>-</span>}
                  value={otp}
                  onChange={setOtp}
                  length={6}
                />
              </Box>
            )}
            <LoadingButton
              type="submit"
              loading={showOtpInput ? isOtpLoading : isLoading}
              loadingPosition="start"
              startIcon={showOtpInput ? <VerifiedUserIcon /> : <LoginRounded />}
              variant="contained"
              fullWidth
              disabled={showOtpInput && otp.length !== 6}
              size="medium"
            >
              {getFormActionText()}
            </LoadingButton>
            <Box>
              <SocialLinkBtn />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            zIndex: 1,
            top: 0,
            left: 0,
          }}
        >
          <RetroGridBg />
        </Box>
      </Box>

      {!pricePagePopup && !isSmallScreen && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          sx={{
            width: { md: 1 / 2 },
            position: "relative",
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            p: 3,
          }}
        >
          <Box
            sx={{
              width: 1,
              height: 1,
              position: "absolute",
              background: "linear-gradient(90deg, #472ed100 0%, #472ed1 100%)",
              zIndex: 0,
            }}
          >
            <img
              src={wave_lins}
              alt="Wave"
              style={{
                height: "100%",
                width: "100%",
                objectFit: "cover",
                mixBlendMode: "overlay",
              }}
              onError={(e) => {
                e.onerror = null;
                e.target.src = require("../assets/wave-line.png");
              }}
            />
          </Box>

          <Box
            sx={{
              width: 1,
              height: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
              color: "white",
              textAlign: "center",
              p: 3,
            }}
          >
            <Box sx={{ height: "20rem", mb: 4 }}>
              <img
                src={signupImg}
                alt="sign-up"
                style={{
                  height: "100%",
                  width: "100%",
                  objectFit: "contain",
                  filter: "brightness(1.1)",
                }}
                onError={(e) => {
                  e.onerror = null;
                  e.target.src = require("../assets/no-preview.webp");
                }}
              />
            </Box>
            <Typography
              variant="body1"
              sx={{ mb: 1, fontSize: "1.4rem", fontWeight: 600 }}
            >
              Create Your Law Firm Account
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "0.9rem" }}>
              Sign up to streamline cases, manage clients, and simplify your
              legal practice.
            </Typography>
          </Box>
        </Box>
      )}

      <Dialog
        open={openForgotPass}
        onClose={handleCloseForgotPass}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2, p: 2 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="div" fontWeight="bold">
            {isEmailSent ? null : "Reset Password"}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseForgotPass}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {isEmailSent ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Box sx={{ height: "10rem" }}>
                <img
                  src={emailSend}
                  alt="Email Sent"
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "contain",
                  }}
                  onError={(e) => {
                    e.onerror = null;
                    e.target.src = require("../assets/no-preview.webp");
                  }}
                />
              </Box>
              <Typography
                variant="body1"
                color="primary.main"
                sx={{ pb: 2, fontSize: "1.2rem", fontWeight: "500" }}
              >
                Check your email
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontSize: "0.9rem" }}
              >
                We've sent a password reset link to your email address. Please
                check your inbox and follow the instructions to reset your
                password.
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontSize: "0.8rem" }}
              >
                {forgotWatch("email")}
              </Typography>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={forgotHandleSubmit(handleForgotPassword)}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enter your email address and we'll send you a link to reset your
                password.
              </Typography>

              <TextField
                fullWidth
                size="small"
                label="Email Address"
                variant="outlined"
                error={!!forgotErrors.email}
                helperText={forgotErrors.email?.message}
                {...forgotRegister("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
                sx={{ mb: { xs: 6, sm: 3 } }}
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCloseForgotPass}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isLoading}
                  loadingPosition="start"
                  startIcon={<SendRoundedIcon />}
                >
                  Send Reset Link
                </LoadingButton>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SignupPage;

const grey = {
  50: "#F3F6F9",
  100: "#E5EAF2",
  200: "#DAE2ED",
  300: "#C7D0DD",
  400: "#B0B8C4",
  500: "#9DA8B7",
  600: "#6B7A90",
  700: "#434D5B",
  800: "#303740",
  900: "#1C2025",
};

const InputElement = styled("input")(
  ({ theme }) => `
    width: 2.5rem;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5;
    padding: 8px 0;
    border-radius: 8px;
    text-align: center;
    @media (max-width: 600px) {
      width: 2rem;
      font-size: 0.75rem;
      padding: 6px 0;
    }
    color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
    background: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
    border: 1px solid #0000003b;
    box-shadow: 0 2px 4px ${
      theme.palette.mode === "dark" ? "rgba(0,0,0, 0.5)" : "rgba(0,0,0, 0.05)"
    };

    &:hover {
      border-color: #1C2025;
    }

    &:focus {
      border-color: #472ed1;
    }

    /* firefox */
    &:focus-visible {
      outline: 0;
    }
  `
);
